import {
  ClientTerritorialAuthorityTypeEnum,
  type ClientTerritorialAuthority
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Text } from 'react-native-paper';

import { FamilyHomeIcon } from '@/assets';
import { useBottomModal } from '@/features/popup/provider/useBottomModal';
import { ReadMoreText } from '@/features/start/components/components/ReadMoreText';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

const styles = StyleSheet.create({
  modalText: {
    paddingHorizontal: 16,
    paddingVertical: 8
  }
});

type GemeindeCardProps = {
  territorialAuthority?: ClientTerritorialAuthority;
};

export const GemeindeCard: FC<GemeindeCardProps> = ({
  territorialAuthority
}) => {
  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'components'
  });
  const { showBottomModal } = useBottomModal();

  const isOrganization =
    territorialAuthority?.type ===
    ClientTerritorialAuthorityTypeEnum.Organization;

  const isGeoAreaProfile =
    territorialAuthority?.type ===
    ClientTerritorialAuthorityTypeEnum.GeoAreaProfile;

  const organization = isOrganization
    ? territorialAuthority.organization
    : undefined;
  const geoAreaProfile = isGeoAreaProfile
    ? territorialAuthority.geoAreaProfile
    : undefined;
  const geoArea = isGeoAreaProfile ? territorialAuthority.geoArea : undefined;

  const organizationTitle = organization?.name;
  const geoAreaProfileTitle =
    geoArea?.name ||
    geoAreaProfile?.description?.trim() ||
    t('gemeinde.defaultTitle');
  const title = organizationTitle || geoAreaProfileTitle;

  const organizationDescription = organization?.description;
  const geoAreaProfileDescription = geoAreaProfile?.description?.trim();
  const description =
    organizationDescription || geoAreaProfileDescription || '';

  const handleReadMore = useCallback(() => {
    if (!description) return;
    showBottomModal({
      title,
      offsetCloseButton: true,
      children: (
        <Text variant="bodyLarge" style={styles.modalText}>
          {description}
        </Text>
      )
    });
  }, [description, showBottomModal, title]);

  if (organization) {
    return (
      <SectionContent
        title={organization.name}
        testId="card-gemeinde"
        titleVariant="headlineSmall"
        icon={FamilyHomeIcon}
      >
        <ReadMoreText
          text={organization.description ?? ''}
          maxLines={4}
          onMore={handleReadMore}
        />
      </SectionContent>
    );
  }

  if (geoAreaProfile) {
    return (
      <SectionContent
        title={geoAreaProfileTitle}
        testId="card-gemeinde"
        titleVariant="headlineSmall"
        icon={FamilyHomeIcon}
      >
        <ReadMoreText
          text={geoAreaProfileDescription || geoArea?.name || ''}
          maxLines={4}
          onMore={handleReadMore}
        />
      </SectionContent>
    );
  }
  return (
    <SectionContent
      title={t('gemeinde.defaultTitle')}
      testId="card-gemeinde"
      titleVariant="headlineSmall"
      icon={FamilyHomeIcon}
    >
      <Text variant="bodyLarge">{t('gemeinde.defaultText')}</Text>
    </SectionContent>
  );
};

GemeindeCard.displayName = 'GemeindeCard';
